import type { HTMLAttributes, InputHTMLAttributes } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FieldCopy, FieldKey, LocaleKey } from "@/i18n";
import { getFieldCopy } from "@/i18n";
import type { CdcOption } from "@/i18n/cdc-options";

export type CdcFieldCopy = FieldCopy & {
  label: string;
  placeholder?: string;
  description?: string;
  prefixWarningTitle?: string;
  prefixWarningDescription?: string;
  errors?: Record<string, string>;
};

export interface CdcFieldBaseProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  locale: LocaleKey;
  name: TName;
  required?: boolean;
}

export interface CdcTextInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends CdcFieldBaseProps<TFieldValues, TName> {
  fieldKey: FieldKey;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  autoComplete?: InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  pattern?: string;
}

export function CdcTextInputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  autoComplete,
  control,
  fieldKey,
  inputMode,
  locale,
  maxLength,
  name,
  pattern,
  required = false,
  type = "text",
  className,
  ...htmlProps
}: Readonly<CdcTextInputFieldProps<TFieldValues, TName>>) {
  const copy = getFieldCopy(locale, fieldKey) as unknown as CdcFieldCopy;
  const requiredLabel = required ? (copy.errors?.required ?? "Required") : null;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div>
            <FormLabel className="inline-block leading-snug">
              {copy.label}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {copy.description ? (
              <FormDescription>{copy.description}</FormDescription>
            ) : null}
          </div>
          <FormControl>
            <Input
              aria-required={required ? "true" : "false"}
              autoComplete={autoComplete}
              inputMode={inputMode}
              maxLength={maxLength}
              pattern={pattern}
              placeholder={copy.placeholder}
              type={type}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export interface CdcSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends CdcFieldBaseProps<TFieldValues, TName> {
  fieldKey: FieldKey;
  options: ReadonlyArray<CdcOption>;
  placeholderOverride?: string;
}

export function CdcSelectField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  fieldKey,
  locale,
  name,
  options,
  placeholderOverride,
  required = false,
  className,
  ...htmlProps
}: Readonly<CdcSelectFieldProps<TFieldValues, TName>>) {
  const copy = getFieldCopy(locale, fieldKey) as unknown as CdcFieldCopy;
  const placeholder = placeholderOverride ?? copy.placeholder;
  const requiredLabel = required ? (copy.errors?.required ?? "Required") : null;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div>
            <FormLabel className="inline-block leading-snug">
              {copy.label}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {copy.description ? (
              <FormDescription>{copy.description}</FormDescription>
            ) : null}
          </div>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
